import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  aiCreditsService,
  type AiCreditBalance,
  type AiCreditPack,
  type AiCreditTransaction,
} from "@/services/aiCreditsService";

// Slice dédié (non persisté) : le solde de crédits est consommé par trois
// surfaces indépendantes du chat (SubscriptionTab, QuotaIndicator,
// LimitReachedCard) — il ne dépend pas d'une conversation active.
interface AiCreditsState {
  balance: AiCreditBalance | null;
  packs: AiCreditPack[];
  history: AiCreditTransaction[];
  loading: boolean;
  purchasingPackId: string | null;
  error: string | null;
}

const initialState: AiCreditsState = {
  balance: null,
  packs: [],
  history: [],
  loading: false,
  purchasingPackId: null,
  error: null,
};

export const fetchCreditBalance = createAsyncThunk<AiCreditBalance>(
  "aiCredits/fetchBalance",
  async () => aiCreditsService.getBalance(),
);

export const fetchCreditPacks = createAsyncThunk<AiCreditPack[]>(
  "aiCredits/fetchPacks",
  async () => aiCreditsService.getPacks(),
);

export const fetchCreditHistory = createAsyncThunk<AiCreditTransaction[]>(
  "aiCredits/fetchHistory",
  // Débits inclus : l'utilisateur doit voir OÙ passent ses crédits, pas
  // seulement ses achats.
  async () => aiCreditsService.getHistory({ limit: 20, includeDebits: true }),
);

const aiCreditsSlice = createSlice({
  name: "aiCredits",
  initialState,
  reducers: {
    setPurchasingPackId: (state, action: { payload: string | null }) => {
      state.purchasingPackId = action.payload;
    },
    setCreditBalance: (state, action: { payload: AiCreditBalance }) => {
      state.balance = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreditBalance.fulfilled, (state, action) => {
        state.balance = action.payload;
        state.error = null;
      })
      .addCase(fetchCreditPacks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreditPacks.fulfilled, (state, action) => {
        state.loading = false;
        state.packs = action.payload;
      })
      .addCase(fetchCreditPacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Erreur de chargement";
      })
      .addCase(fetchCreditHistory.fulfilled, (state, action) => {
        state.history = action.payload;
      });
  },
});

export const { setPurchasingPackId, setCreditBalance } = aiCreditsSlice.actions;
export default aiCreditsSlice.reducer;
